/**
 * Chat with Documents Action
 * Opens the chat interface with selected file(s)
 *
 * For files: passes the fileId (taskId/blobId)
 * For folders: passes the folder path as an array of strings
 *
 * The chat page handles resolving folder paths to their contained files.
 */

import { Datum } from "../datastructure";
import { ActionDefinition, ActionContext, getFilePayload } from "./types";
import chatWithDocumentsService, {
  SelectedFileInfo,
} from "@/components/FpAppV4/services/chatWithDocuments";
import { fetchFiles } from "../fetchFiles";

/** The task statuses a file can be chatted with: processed, or failed (the
 *  agent reads a failed file's stored bytes, #13887). A file still being
 *  processed cannot be yet. The context menu and the bulk-chat gate share it. */
const CHATTABLE_STATUSES = ["completed", "partially-completed", "failed"];
export const isChattableFileStatus = (status: string | undefined): boolean =>
  CHATTABLE_STATUSES.includes(status || "");

/** The file a chat is handed, marked when its processing failed so its chip
 *  says so (#13887). */
export const chatFileInfo = (
  id: string,
  name: string,
  type: string,
  status: string | undefined
): SelectedFileInfo => ({
  id,
  name,
  type,
  ...(status === "failed" ? { processingFailed: true } : {}),
});

/**
 * Callback type for chat with documents
 * Called before opening chat. Return false to cancel the action.
 */
export type ChatWithDocumentsCallback = (params: {
  fileIds: string[];
  folderPaths: string[][];
}) => boolean | void;

/**
 * Chat with document(s) action
 * Opens a new chat workspace with the selected file(s)
 */
export async function chatWithDocuments(
  datum: Datum,
  context: ActionContext & { onChatWithDocuments?: ChatWithDocumentsCallback }
): Promise<void> {
  const fileIds: string[] = [];
  const files: SelectedFileInfo[] = [];
  const folderPaths: string[][] = [];

  if (datum.type === "file") {
    const payload = getFilePayload(datum);
    if (payload?.fileId && isChattableFileStatus(payload.status)) {
      fileIds.push(payload.fileId);
      files.push(
        chatFileInfo(
          payload.fileId,
          payload.fileName,
          payload.fileType,
          payload.status
        )
      );
    }
  } else {
    // Ensure path includes drive prefix for modal compatibility.
    // The Documents page may be in single-drive mode (no prefix),
    // but the file selection modal uses across-drives mode (has prefix).
    const driveId = datum.payload?.driveId || context.driveId;
    const needsPrefix = !context.filetree?.shouldStripDrivePrefix && driveId;
    const folderPath = needsPrefix ? [driveId!, ...datum.path] : datum.path;
    folderPaths.push(folderPath);
    const listing = await fetchFiles({
      pathPrefix: datum.path,
      maxDepth: 50,
    });
    for (const file of listing.fileList) {
      if (!isChattableFileStatus(file.status)) continue;
      fileIds.push(file.fileId);
      files.push(
        chatFileInfo(file.fileId, file.fileName, file.fileType, file.status)
      );
    }
  }

  if (fileIds.length === 0 && folderPaths.length === 0) {
    context.showToast?.("Cannot chat with this item", "error");
    return;
  }

  // Allow parent to intercept/cancel the action
  if (context.onChatWithDocuments) {
    const shouldProceed = context.onChatWithDocuments({ fileIds, folderPaths });
    if (shouldProceed === false) {
      return;
    }
  }

  await chatWithDocumentsService({ fileIds, folderPaths, files });
}

/**
 * Action definition for context menu - files
 */
export const chatWithDocumentAction: ActionDefinition = {
  config: {
    action: "chat-with-document",
    label: "Chat with document",
  },
  isApplicable: (datum) =>
    datum.type === "file" &&
    datum.payload?.type === "file" &&
    datum.payload.fileType !== "unsupported" &&
    datum.payload.fileType !== "unreadable" &&
    datum.payload.fileType !== "empty" &&
    isChattableFileStatus(datum.payload.status),
  execute: chatWithDocuments,
};

/**
 * Action definition for context menu - folders
 */
export const chatWithDocumentsAction: ActionDefinition = {
  config: {
    action: "chat-with-documents",
    label: "Chat with documents",
  },
  isApplicable: (datum) => datum.type === "folder",
  execute: chatWithDocuments,
};
